/**
 * UX-side expansions for `ux_filter_*` sentinel schemaProperties.
 *
 * These sentinel names from the Atlas Filters spec are NOT real cube columns —
 * they signal that the UX must compose a compound cube filter over real
 * underlying columns, per the spec's "Applied Filter Text" column. The
 * `ux_filter_` prefix is load-bearing: it lets CubeFilterBuilder detect the
 * sentinel without accidentally rewriting real cube column names.
 *
 * Expansion entry fields:
 *   - combinator: "or" | "and" — how the underlying filters are combined
 *   - parts: [{ schemaProperty }] — real cube columns to fan out to
 *   - operator (optional): overrides the operator derived from filterType
 *     (used when the UX checkbox semantics differ from the spec's intended
 *     SQL — e.g. "Email Verified" is HAS_VALUE in the UI but spec wants
 *     `= 'Verified'`, so we override to `equals`)
 *   - values (optional): overrides the values derived from f.value
 */
export const UX_FILTER_EXPANSIONS = {
    ux_filter_profile_has_phone: {
        combinator: "or",
        parts: [
            { schemaProperty: "company_has_phone" },
            { schemaProperty: "contact_has_phone" },
        ],
    },
    ux_filter_profile_has_email: {
        combinator: "or",
        parts: [
            { schemaProperty: "company_has_email" },
            { schemaProperty: "contact_has_email" },
        ],
    },
    ux_filter_profile_has_verified_email: {
        combinator: "or",
        operator: "equals",
        values: ["Verified"],
        parts: [
            { schemaProperty: "company_email_status" },
            { schemaProperty: "contact_email_status" },
        ],
    },
    ux_filter_profile_email_status: {
        combinator: "or",
        parts: [
            { schemaProperty: "company_email_status" },
            { schemaProperty: "contact_email_status" },
        ],
    },
    // Contacts-context counterpart of has_verified_email — narrower than the
    // Profile version (contact-only, no company OR'd in). Degenerate
    // single-child compound just to carry the operator/value override
    // (HAS_VALUE in the UI but spec wants `= 'Verified'`).
    ux_filter_contact_has_verified_email: {
        combinator: "or",
        operator: "equals",
        values: ["Verified"],
        parts: [{ schemaProperty: "contact_email_status" }],
    },
};

export function isUxFilterSentinel(schemaProperty) {
    return (
        typeof schemaProperty === "string" &&
        schemaProperty.startsWith("ux_filter_")
    );
}
